'use client';

/**
 * Surfaced on a board page whose initial `initializeSeats` flow died
 * mid-sequence (i.e. `seatsInitialized < totalSeats` on the V21 manager).
 *
 * Visible to **all** viewers — not just the creator — because the
 * indexer's `GridPricesInitialized` handler optimistically materialises
 * every seat as creator-held the moment `createGrid` lands, which makes
 * the canvas paint a fully-populated board even before any
 * `initializeSeats` batch confirms. Non-creators clicking a seat would
 * otherwise open a buyout drawer and watch the on-chain `addBatch` call
 * revert with `GridNotFullyInitialized()` — the banner replaces that
 * silent-revert UX with an explicit "this board isn't ready" notice.
 *
 * For the creator, the banner also embeds the resume CTA inline (see
 * `CreatorResumeCTA` below): it reads `seatsInitialized` from the
 * chain, slices the persisted price array off the on-chain count
 * forward, and submits the remaining batches via the standard
 * transaction queue. The CTA is gated on a localStorage entry from
 * the original launch flow today (see `pending-seat-init.ts`); a
 * chain/indexer-derived fallback for different-device or
 * cleared-storage cases lands separately.
 */

import { useCallback, useEffect, useState } from 'react';
import { encodeFunctionData, type Hex } from 'viem';

import { useTransactionQueue } from '@/hooks/use-transactions';
import { clientPublicClient } from '@/lib/viem-client';
import { UGM_ABI } from '@/lib/contracts/abi';
import { parseGridId } from '@/lib/utils';

import {
  clearPendingSeatInit,
  getPendingSeatInit,
  type PendingSeatInitEntry,
} from './state/pending-seat-init';

// Mirror of `use-launch-grid.ts` — keep these in sync. ~4M gas per batch
// at 32 seats × ~115–130k gas/seat, well under any wallet sim cap. The
// `noBatch` flag on each ActionQueueItem keeps ERC-5792 wallets from
// collecting these into one bundled tx (the original 256-tile failure).
const SEAT_INIT_BATCH_SIZE = 32;

const UGM_INIT_SEATS_ABI = [
  {
    name: 'initializeSeats',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'gridId', type: 'uint256' },
      { name: 'prices', type: 'uint128[]' },
    ],
    outputs: [],
  },
] as const;

function chunk<T>(arr: T[], size: number): T[][] {
  if (size <= 0) return [arr];
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size));
  }
  return out;
}

interface BoardInitializingBannerProps {
  /** On-chain `seatsInitialized` count, sourced from the API payload's
   *  `grid.seatsInitialized`. Treated as "fully initialized" when null /
   *  undefined / equal to `totalSeats` so the banner never false-fires
   *  on V2 base grids or transient RPC blips (the API's multicall
   *  defaults to `totalSeats` on failure for exactly this reason). */
  seatsInitialized: number | undefined | null;
  /** Configured total seat count from `grid.totalSeats`. */
  totalSeats: number | undefined | null;
  /** True for the grid creator — when set, the banner appends the
   *  "Finish initialization" CTA below the info text. Non-creators see
   *  the info-only variant. */
  isCreator?: boolean;
  /** Composite grid id (`${ugm}-${numericId}`). Required for the
   *  resume CTA path; harmless to pass for non-creator views. */
  compositeGridId?: string | null | undefined;
  /**
   * Indexer-derived per-seat prices, ordered by `seatIndex` ascending,
   * length equal to `totalSeats`. Used as the resume fallback when the
   * launch wizard's `localStorage` entry is missing — different device,
   * cleared storage, or past the 7-day TTL.
   *
   * The indexer's `GridPricesInitialized` handler captures the full
   * `prices` array on `createGrid` and writes it onto every `GridSeat`,
   * so this is available for the partial-init case the banner exists
   * to handle. Pass `gridPage.rawSeats.map(s => BigInt(s.selfAssessedPrice))`
   * (after sorting by `seatIndex`); the banner re-validates length
   * before using it.
   */
  indexerSeatPrices?: bigint[] | null;
  className?: string;
}

export function BoardInitializingBanner({
  seatsInitialized,
  totalSeats,
  isCreator,
  compositeGridId,
  indexerSeatPrices,
  className,
}: BoardInitializingBannerProps) {
  // Self-conceal aggressively — the banner's job is to surface a
  // genuine partial-init state, not noise on every healthy board. Any
  // missing field (loading, RPC blip, V2 base grid that doesn't track
  // `seatsInitialized`) is treated as "fully initialized".
  if (
    seatsInitialized == null ||
    totalSeats == null ||
    seatsInitialized >= totalSeats
  ) {
    return null;
  }

  const remaining = totalSeats - seatsInitialized;

  return (
    <div
      className={
        className ??
        'rounded-[6px] border border-amber-500/40 bg-amber-500/10 backdrop-blur-md p-3 text-[11px] text-amber-50 space-y-2'
      }
    >
      <div className="space-y-1">
        <p className="font-mono uppercase tracking-[0.2em] text-amber-200 text-[9px]">
          Board still initializing
        </p>
        <p className="text-amber-50/90">
          {seatsInitialized} of {totalSeats} seats are ready on-chain.
          Buys and claims are paused for everyone until the remaining{' '}
          {remaining} are written. The creator can finish setup from
          their tools panel.
        </p>
      </div>
      {isCreator && compositeGridId ? (
        <CreatorResumeCTA
          compositeGridId={compositeGridId}
          indexerSeatPrices={indexerSeatPrices ?? null}
          totalSeats={totalSeats}
        />
      ) : null}
    </div>
  );
}

/**
 * Creator-only "Finish initialization" affordance. Splits out so the
 * outer info banner stays a pure presentation component for the
 * common (non-creator) path.
 *
 * Resolves the source of truth for the remaining `prices` argument
 * to `initializeSeats` in this priority order:
 *   1. `localStorage[takeover:pending-seat-init:v1]` — the wizard
 *      stashes the full `seatPrices` array here before firing the
 *      original batches. Fast, exact, and keyed to the same composite
 *      id we're rendering for; persists 7 days
 *      (`MAX_AGE_MS` in `pending-seat-init.ts`).
 *   2. `indexerSeatPrices` — derived from `gridPage.rawSeats` by the
 *      caller. The indexer's `GridPricesInitialized` handler writes
 *      every seat's `selfAssessedPrice` from the full `prices` array
 *      emitted by `createGrid`, so this is available even when the
 *      creator opens the board on a fresh device or after clearing
 *      site data — exactly the cases the localStorage path
 *      misses.
 *
 * In both paths we slice from the on-chain `seatsInitialized` count
 * forward, so a partial-failure resume picks up cleanly without
 * re-writing already-init seats (the contract advances the count
 * exactly by the length of each `prices` argument).
 *
 * Each step is flagged `noBatch: true` so ERC-5792 wallets execute
 * them sequentially via `eth_sendTransaction` instead of bundling
 * them into one atomic `wallet_sendCalls` request — the same
 * opt-out the original launch path uses, since bundling is exactly
 * what bricked 256-seat launches at ~30M gas.
 */
function CreatorResumeCTA({
  compositeGridId,
  indexerSeatPrices,
  totalSeats,
}: {
  compositeGridId: string;
  indexerSeatPrices: bigint[] | null;
  totalSeats: number | undefined | null;
}) {
  const { executeActions } = useTransactionQueue();
  const [pending, setPending] = useState<PendingSeatInitEntry | null>(null);
  const [seatsInitialized, setSeatsInitialized] = useState<number | null>(null);
  const [isResuming, setIsResuming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Decompose the composite gridId once; harmless to re-run on every
  // render but the resume CTA reads `managerAddress` / `numericId`
  // from this regardless of whether `pending` exists.
  const { managerAddress, numericId } = parseGridId(compositeGridId);

  // Resume is only viable when the caller passes a complete prices
  // array (length === totalSeats). Trimmed / partial arrays would
  // misalign the `seatsInitialized` slice below and we'd write the
  // wrong prices for the trailing seats — bail to the "no recovery"
  // message instead.
  const indexerPricesUsable =
    indexerSeatPrices != null &&
    typeof totalSeats === 'number' &&
    indexerSeatPrices.length === totalSeats;

  useEffect(() => {
    setPending(getPendingSeatInit(compositeGridId));
  }, [compositeGridId]);

  // Always read on-chain `seatsInitialized` regardless of which source
  // we'll use for prices — the chain count is the authoritative slice
  // origin. Without this, the indexer-only fallback couldn't compute
  // `remaining = totalSeats - seatsInitialized`.
  useEffect(() => {
    if (!managerAddress || !numericId) {
      setSeatsInitialized(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = (await clientPublicClient.readContract({
          address: managerAddress as Hex,
          abi: UGM_ABI,
          functionName: 'seatsInitialized',
          args: [BigInt(numericId)],
        })) as number;
        if (cancelled) return;
        const count = Number(result);
        setSeatsInitialized(count);
        if (pending && count >= pending.totalSeats) {
          // Stale localStorage entry (resumed from another device,
          // or the count caught up some other way). Drop it so the
          // banner doesn't keep showing a CTA that has nothing to do.
          clearPendingSeatInit(pending.compositeGridId);
          setPending(null);
        }
      } catch (err) {
        if (cancelled) return;
        console.warn('[BoardInitializingBanner] seatsInitialized read failed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [managerAddress, numericId, pending]);

  const resumeInit = useCallback(async () => {
    if (seatsInitialized === null || !managerAddress || !numericId) return;
    const effectiveTotal = pending?.totalSeats ?? totalSeats;
    if (typeof effectiveTotal !== 'number') return;
    if (seatsInitialized >= effectiveTotal) return;

    // Source the full prices array from localStorage when present
    // (exact, includes any non-uniform pricing from the wizard) and
    // from the indexer otherwise. Both arrays are 0-indexed by
    // seatIndex with length === totalSeats, so the slice math is
    // identical regardless of source.
    const fullPrices: bigint[] | null = pending
      ? pending.seatPrices.map((p) => BigInt(p))
      : indexerPricesUsable
        ? indexerSeatPrices!
        : null;

    if (!fullPrices) return;

    setError(null);
    setIsResuming(true);

    try {
      const remaining = fullPrices.slice(seatsInitialized);

      const batches = chunk(remaining, SEAT_INIT_BATCH_SIZE);
      const actions = batches.map((batch, idx) => {
        const startSeat = seatsInitialized + idx * SEAT_INIT_BATCH_SIZE;
        const endSeat = startSeat + batch.length - 1;
        return {
          id: `resume-seats-${idx}`,
          label:
            batches.length === 1
              ? 'Finish seat initialization'
              : `Initialize seats ${idx + 1}/${batches.length}`,
          description: `Seats ${startSeat + 1}–${endSeat + 1}`,
          call: {
            to: managerAddress as Hex,
            data: encodeFunctionData({
              abi: UGM_INIT_SEATS_ABI,
              functionName: 'initializeSeats',
              args: [BigInt(numericId), batch],
            }),
          },
          // Same opt-out as the original launch path so the wallet
          // doesn't 5792-bundle these into a single ~30M-gas tx.
          noBatch: true,
        };
      });

      const outcome = await executeActions(
        'Finish board initialization',
        actions,
      );

      if (!outcome.success) {
        throw new Error('Seat initialization cancelled or failed');
      }

      if (pending) {
        clearPendingSeatInit(pending.compositeGridId);
        setPending(null);
      }
      setSeatsInitialized(effectiveTotal);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Resume initialization failed';
      setError(message);
    } finally {
      setIsResuming(false);
    }
  }, [
    pending,
    seatsInitialized,
    executeActions,
    indexerSeatPrices,
    indexerPricesUsable,
    managerAddress,
    numericId,
    totalSeats,
  ]);

  if (seatsInitialized === null) {
    return (
      <p className="text-amber-50/70 text-[10px]">
        Reading on-chain progress…
      </p>
    );
  }

  const effectiveTotal = pending?.totalSeats ?? totalSeats;
  if (typeof effectiveTotal !== 'number') {
    // No reliable target seat count; can't compute the remaining slice.
    // Don't render a useless CTA.
    return null;
  }

  if (seatsInitialized >= effectiveTotal) {
    // Race with the chain read — banner self-conceals on the next
    // render once the parent re-fetches `grid.seatsInitialized`.
    return null;
  }

  const hasResumeSource = !!pending || indexerPricesUsable;
  if (!hasResumeSource) {
    return (
      <p className="text-amber-50/70 text-[10px]">
        Couldn&apos;t recover the original price list for the remaining
        seats. Open the board on the device you launched from, or wait
        a few minutes for the indexer to catch up.
      </p>
    );
  }

  const remaining = effectiveTotal - seatsInitialized;
  const sourceLabel = pending ? null : 'using indexer-derived prices';

  return (
    <>
      {error ? (
        <p className="text-rose-200 text-[10px]">{error}</p>
      ) : null}
      <button
        type="button"
        disabled={isResuming}
        onClick={() => void resumeInit()}
        className="w-full py-1.5 rounded-[3px] bg-amber-500/30 border border-amber-400/50 text-[10px] font-mono uppercase tracking-wide text-amber-50 hover:bg-amber-500/40 disabled:opacity-50"
      >
        {isResuming ? 'Initializing…' : `Finish initialization (${remaining} seats)`}
      </button>
      {sourceLabel ? (
        <p className="text-amber-50/60 text-[10px] text-center">{sourceLabel}</p>
      ) : null}
    </>
  );
}
